// Pure chip-visual mapping for the batch questionnaire chips (Compendium chip-color contract).
// Separates the THREE orthogonal facts a chip carries onto DIFFERENT visual channels so they
// stop colliding (the old code overloaded one color slot: primary == selected AND warning ==
// editing, and this theme's --color-primary is orange, so "selected" looked like "warning"):
//   • VALUE      -> fill + color + icon   (Yes pops; No/Unknown/unanswered recede)
//   • CONFIRMED  -> opacity + border-style (unconfirmed default = dimmed + dashed)  [CAPA F123]
//   • EDITING    -> a ring (NOT a fill swap, so it never collides with the value color)
//
// Everything is expressed as flip-per-theme CSS tokens (var(--color-*)) + theme-AGNOSTIC
// structure (fill/outline, solid/dashed, opacity, icon, ring) — so light and dark both work
// from this one mapping. NEVER hardcode hex here.
//
// "No must be subtle" (Dr. Deo): in a ROS most answers are No, so No/deny-all render quiet —
// red is reserved for genuine alerts, never for a routine pertinent-negative.

export type ChipValueClass = 'yes' | 'no' | 'unknown' | 'unanswered'
export type ChipKind = 'member' | 'deny_all'
export type ChipIcon = 'check' | 'minus' | 'question' | 'empty' | 'deny_all'

export interface ChipVisual {
  intent: ChipValueClass | 'deny_all_applied' | 'deny_all_available'
  filled: boolean
  fillToken: string | null     // CSS custom property name, or null for no fill
  borderToken: string
  borderStyle: 'solid' | 'dashed'
  textToken: string
  icon: ChipIcon
  opacity: number
  ring: boolean                // editing highlight
  ringToken: string | null
  bold: boolean
}

// Map a raw first_response to a value class. Scale: 5/4 = yes, 2/1 = no, 3 = neutral(unknown).
// Radio: Yes / No / Unknown. null/'' = unanswered. ('Unknown' is NOT a real answer — see B8.)
export function classifyChipValue(firstResponse: any): ChipValueClass {
  if (firstResponse === null || firstResponse === undefined || firstResponse === '') return 'unanswered'
  const v = String(firstResponse)
  if (v === 'Yes' || v === '5' || v === '4') return 'yes'
  if (v === 'No' || v === '1' || v === '2') return 'no'
  return 'unknown' // 'Unknown', '3', or any non-standard value
}

interface ChipVisualOpts {
  confirmed?: boolean        // false = unconfirmed default (F123) -> dimmed + dashed
  editing?: boolean          // inline editor open on this chip -> ring
  kind?: ChipKind            // 'deny_all' = the group-level control, styled distinctly
  denyAllApplied?: boolean   // for kind='deny_all': whole group denied
}

export function chipVisualState(firstResponse: any, opts: ChipVisualOpts = {}): ChipVisual {
  const { confirmed = true, editing = false, kind = 'member', denyAllApplied = false } = opts

  // The group-level deny-all CONTROL is an action, not a member value. When applied it reads
  // CALM (muted), never red — so a fully-negative ROS doesn't become a sea of alarm color.
  if (kind === 'deny_all') {
    return denyAllApplied
      ? { intent: 'deny_all_applied', filled: true, fillToken: '--color-surface-muted',
          borderToken: '--color-border-medium', borderStyle: 'solid', textToken: '--color-text-muted',
          icon: 'check', opacity: 1, ring: editing, ringToken: editing ? '--color-primary-dark' : null, bold: false }
      : { intent: 'deny_all_available', filled: false, fillToken: null,
          borderToken: '--color-border-medium', borderStyle: 'solid', textToken: '--color-text-secondary',
          icon: 'deny_all', opacity: 1, ring: false, ringToken: null, bold: false }
  }

  const value = classifyChipValue(firstResponse)
  const byValue: Record<ChipValueClass, Pick<ChipVisual, 'filled' | 'fillToken' | 'borderToken' | 'textToken' | 'icon' | 'bold'>> = {
    // Yes POPS — the only filled member state.
    yes:        { filled: true,  fillToken: '--color-primary', borderToken: '--color-primary',      textToken: '--color-btn-primary-text', icon: 'check',    bold: true },
    // No RECEDES — outlined, muted, small minus.
    no:         { filled: false, fillToken: null,              borderToken: '--color-border-medium', textToken: '--color-text-muted',       icon: 'minus',    bold: false },
    // Unknown — neutral but distinct from No via the '?' glyph.
    unknown:    { filled: false, fillToken: null,              borderToken: '--color-border-light',  textToken: '--color-text-muted',       icon: 'question', bold: false },
    // Unanswered — faintest; empty circle.
    unanswered: { filled: false, fillToken: null,              borderToken: '--color-border-light',  textToken: '--color-text-muted',       icon: 'empty',    bold: false },
  }
  const base = byValue[value]
  const unconfirmed = !confirmed && value !== 'unanswered'  // a default value showing but not committed
  return {
    intent: value,
    ...base,
    // Dashed = "not committed" (unconfirmed default) OR "nothing here yet" (unanswered).
    borderStyle: (unconfirmed || value === 'unanswered') ? 'dashed' : 'solid',
    // Dim an unconfirmed default so it reads as tentative.
    opacity: unconfirmed ? 0.7 : 1,
    // Editing rides on a ring, never a fill swap — so value color stays readable.
    ring: editing,
    ringToken: editing ? '--color-primary-dark' : null,
  }
}
